import type { EnvironmentConfig } from 'models/environmentConfig';
import { type RuntimeEnv, RuntimeEnvKey } from 'models/runtimeEnv';
import type { UserManagerSettings } from 'oidc-client-ts';

const trimTrailingSlashes = (value: string): string => value.replace(/\/+$/, '');

export const readRequiredUrl = (env: RuntimeEnv, name: RuntimeEnvKey): string => {
  const rawValue = env[name];

  if (!rawValue) {
    throw new Error(`Missing required runtime environment variable: ${name}`);
  }

  try {
    const url = new URL(rawValue);
    return trimTrailingSlashes(url.toString());
  } catch {
    throw new Error(`Invalid required runtime environment variable URL: ${name}`);
  }
};

export const createVirtualPATH = (env: RuntimeEnv) => {
  const webUrl = readRequiredUrl(env, RuntimeEnvKey.WEB_URL);
  const { pathname } = new URL(webUrl);

  return pathname === '/' ? '' : trimTrailingSlashes(pathname);
};

export const withBaseName = (url: string | undefined) => {
  return url === '' || url === undefined ? '/' : url;
};

export const buildStsConfigBase = (
  env: RuntimeEnv,
  defaultClientId: string
): Pick<
  UserManagerSettings,
  | 'authority'
  | 'client_id'
  | 'redirect_uri'
  | 'scope'
  | 'post_logout_redirect_uri'
  | 'response_type'
  | 'response_mode'
  | 'automaticSilentRenew'
  | 'silent_redirect_uri'
  | 'loadUserInfo'
> => {
  const securityUrl = readRequiredUrl(env, RuntimeEnvKey.SECURITY_URL);
  const webUrl = readRequiredUrl(env, RuntimeEnvKey.WEB_URL);

  return {
    authority: securityUrl,
    client_id: defaultClientId,
    redirect_uri: `${webUrl}/auth/callback`,
    scope: 'openid profile roles dec-admin.read dec-admin.write offline_access',
    post_logout_redirect_uri: `${webUrl}/`,
    response_type: 'code',
    response_mode: 'query',
    automaticSilentRenew: true,
    silent_redirect_uri: `${webUrl}/silent.html`,
    loadUserInfo: true
  };
};

export const buildEnvConfig = (env: RuntimeEnv, sts: UserManagerSettings): EnvironmentConfig => {
  const virtualPath = createVirtualPATH(env);
  const securityUrl = readRequiredUrl(env, RuntimeEnvKey.SECURITY_URL);
  const applicationUrl = readRequiredUrl(env, RuntimeEnvKey.APPLICATION_URL);
  const webUrl = readRequiredUrl(env, RuntimeEnvKey.WEB_URL);

  return {
    baseUrlApi: `${securityUrl}/api`,
    baseUrlApplication: `${applicationUrl}/api/v1`,
    baseUrlSecurityApi: `${securityUrl}/api`,
    baseUrlApplicationApi: `${applicationUrl}/api/v1`,
    sts: sts,
    serverApplication: applicationUrl,
    development: undefined,
    defaultLang: 'en',
    basename: withBaseName(virtualPath),
    publicUrl: virtualPath,
    appUrl: webUrl,
    profile: env.NODE_ENV,
    version: env.VERSION ?? '3.0',
    theme: env.THEME ?? 'poss',
    sessionIdleTimeoutSeconds: env[RuntimeEnvKey.SESSION_IDLE_TIMEOUT_SECONDS]
  };
};
